import { NavLink } from "react-router";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { type Domain, domainPath } from "./nav";

/**
 * The second column. For most domains it lists sections; Routing and
 * Accounts will replace it with the object list itself (callflows, the
 * account tree) once those screens exist.
 */
export function ContextColumn({ domain, accountId, section, hidden, onNavigate }: { domain: Domain; accountId?: string; section?: string; hidden?: Set<string>; onNavigate?: () => void }) {
  const groups = new Map<string | undefined, Domain["sections"]>();
  for (const s of domain.sections) {
    if (hidden?.has(s.id)) continue;
    const list = groups.get(s.group) ?? [];
    list.push(s);
    groups.set(s.group, list);
  }
  return (
    <aside className="flex h-full w-full flex-col bg-column text-column-foreground">
      <div className="flex h-9 shrink-0 items-center px-3 hairline-b">
        <span className="text-xs font-semibold">{domain.label}</span>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <nav aria-label={`${domain.label} sections`} className="px-1.5 py-2">
          {[...groups.entries()].map(([group, sections]) => (
            <div key={group ?? "_"} className="mb-1.5">
              {group && <div className="eyebrow px-3 pb-0.5 pt-2">{group}</div>}
              {sections.map((s) => {
                const active = s.id === section;
                return (
                  <NavLink
                    key={s.id}
                    to={domainPath(domain, accountId, s.id)}
                    onClick={onNavigate}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "relative mb-0.5 flex min-h-9 items-center gap-2 rounded-md border border-transparent px-2.5 py-1 text-sm focus-visible:outline-2 focus-visible:outline-ring",
                      active ? "bg-navigation-selection font-medium text-navigation-selection-foreground" : "text-foreground/80 hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <span className="truncate">{s.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </ScrollArea>
    </aside>
  );
}
